import React, { useEffect, useMemo, useRef, useState } from 'react';
import io from 'socket.io-client';
import './App.css';

// Socket server URL - use environment variable or fallback to current origin
const SERVER_URL = process.env.REACT_APP_SERVER_URL || window.location.origin;

// Helper function to build API URLs
const apiUrl = (endpoint) => `${SERVER_URL}${endpoint}`;

// Ensure a single socket instance
const socket = (typeof window !== 'undefined' && window._chatSocket)
  ? window._chatSocket
  : (window._chatSocket = io(SERVER_URL, { 
      path: '/socket.io',
      transports: ['websocket', 'polling']
    }));

function App() {
  // Authentication state
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ username: '', password: '', avatar: '' });

  // Chat state
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [selectedChat, setSelectedChat] = useState({ type: null, id: null, title: '' });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredChats, setFilteredChats] = useState({ groups: [], users: [] });
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [unreadDM, setUnreadDM] = useState({});
  const [unreadGroups, setUnreadGroups] = useState({});
  const [toasts, setToasts] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  
  const seenIds = useRef(new Set());
  const messagesEndRef = useRef(null);

  // Authentication handlers
  const handleAuth = async (e) => {
    e.preventDefault();
    const endpoint = authMode === 'login' ? '/login' : '/register';
    const url = apiUrl(endpoint);
    
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authForm)
      });
      const data = await res.json();
      
      if (data.success) {
        const u = data.user || { username: authForm.username, avatar: authForm.avatar };
        setUser(u);
        socket.emit('user online', u.username);
        
        // Load initial data
        loadUserData(u.username);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error('Auth error:', error);
      alert('Connection error');
    }
  };

  const loadUserData = async (username) => {
    try {
      const [groupsRes, usersRes] = await Promise.all([
        fetch(apiUrl(`/groups/${username}`)),
        fetch(apiUrl('/users/all'))
      ]);
      
      const groupsData = await groupsRes.json();
      const usersData = await usersRes.json();
      
      setGroups(groupsData);
      setAllUsers(usersData);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  // Socket event handlers
  useEffect(() => {
    if (!user) return;

    const handleUsersOnline = (users) => {
      setOnlineUsers(users);
    };

    const handleGroupMessage = (msg) => {
      if (msg?._id && seenIds.current.has(msg._id)) return;
      if (msg?._id) seenIds.current.add(msg._id);
      
      if (selectedChat.type === 'group' && selectedChat.id === msg.room) {
        setMessages(prev => [...prev, msg]);
      } else if (msg.from !== user.username) {
        // Message is for a group we're not currently viewing, mark as unread
        setUnreadGroups(prev => ({ 
          ...prev, 
          [msg.room]: (prev[msg.room] || 0) + 1 
        }));
        
        // Show notification toast
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, text: `New message in ${msg.room}` }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
      }
    };

    const handleDM = (msg) => {
      if (msg?._id && seenIds.current.has(msg._id)) return;
      if (msg?._id) seenIds.current.add(msg._id);
      
      const other = msg.from === user.username ? msg.to : msg.from;
      if (selectedChat.type === 'dm' && selectedChat.id === other) {
        setMessages(prev => [...prev, msg]);
      }
    };

    const handleNotification = ({ from, message }) => {
      if (selectedChat.type !== 'dm' || selectedChat.id !== from) {
        setUnreadDM(prev => ({ ...prev, [from]: (prev[from] || 0) + 1 }));
        
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, text: `New message from ${from}` }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
      }
    };

    const handleMessageRead = ({ messageId, username, status }) => {
      setMessages(prev => prev.map(msg => {
        if (msg._id === messageId) {
          if (username) {
            // Group message - add to readBy
            const readBy = msg.readBy || [];
            if (!readBy.some(r => r.username === username)) {
              return { ...msg, readBy: [...readBy, { username, readAt: new Date() }] };
            }
          } else if (status) {
            // DM - update status
            return { ...msg, status };
          }
        }
        return msg;
      }));
    };

    socket.on('users online', handleUsersOnline);
    socket.on('group message', handleGroupMessage);
    socket.on('dm', handleDM);
    socket.on('notification', handleNotification);
    socket.on('message read', handleMessageRead);

    return () => {
      socket.off('users online', handleUsersOnline);
      socket.off('group message', handleGroupMessage);
      socket.off('dm', handleDM);
      socket.off('notification', handleNotification);
      socket.off('message read', handleMessageRead);
    };
  }, [user, selectedChat]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-mark messages as read when viewing chat
  useEffect(() => {
    if (messages.length > 0 && selectedChat.type && user) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.from !== user.username) {
        socket.emit('mark message read', {
          messageId: lastMessage._id,
          username: user.username
        });
      }
    }
  }, [messages, selectedChat, user]);

  // Chat handlers
  const openGroup = async (name) => {
    setSelectedChat({ type: 'group', id: name, title: name });
    setShowGroupDetails(false);
    socket.emit('join group', name);
    
    // Clear unread count for this group
    setUnreadGroups(prev => ({ ...prev, [name]: 0 }));
    
    // Mark chat as read
    socket.emit('mark chat read', {
      chatId: name,
      chatType: 'group',
      username: user.username
    });
    
    try {
      const [messagesRes, groupRes] = await Promise.all([
        fetch(apiUrl(`/messages/group/${name}`)),
        fetch(apiUrl(`/groups/details/${name}`))
      ]);
      
      const messagesData = await messagesRes.json();
      const groupData = await groupRes.json();
      
      setMessages(messagesData);
      setCurrentGroup(groupData);
    } catch (error) {
      console.error('Error opening group:', error);
    }
  };

  const openDM = async (otherUsername) => {
    if (!user) return;
    
    setSelectedChat({ type: 'dm', id: otherUsername, title: otherUsername });
    setShowGroupDetails(false);
    setCurrentGroup(null);
    
    // Mark DM as read
    setUnreadDM(prev => ({ ...prev, [otherUsername]: 0 }));
    socket.emit('mark chat read', {
      chatId: otherUsername,
      chatType: 'dm',
      username: user.username
    });
    
    try {
      const res = await fetch(apiUrl(`/messages/dm/${user.username}/${otherUsername}`));
      const data = await res.json();
      setMessages(data);
    } catch (error) {
      console.error('Error opening DM:', error);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || !user || !selectedChat.type) return;

    if (selectedChat.type === 'group') {
      socket.emit('group message', {
        groupName: selectedChat.id,
        from: user.username,
        message: input.trim()
      });
    } else {
      socket.emit('dm', {
        from: user.username,
        to: selectedChat.id,
        message: input.trim()
      });
    }
    
    setInput('');
  };

  // Group management
  const createGroup = async () => {
    if (!user) return;
    
    const name = prompt('Group name:');
    if (!name?.trim()) return;
    
    const description = prompt('Group description (optional):') || '';
    
    try {
      const res = await fetch(apiUrl('/groups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description, createdBy: user.username })
      });
      
      const data = await res.json();
      if (data.name) {
        setGroups(prev => [...prev, data]);
      } else {
        alert(data.error || 'Failed to create group');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Error creating group');
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    try {
      const res = await fetch(apiUrl(`/users/search/${encodeURIComponent(query)}`));
      const users = await res.json();
      setSearchResults(users.filter(u => u.username !== user?.username));
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const addMemberToGroup = async (username) => {
    if (!currentGroup || !user) return;
    
    try {
      const res = await fetch(apiUrl(`/groups/${encodeURIComponent(currentGroup.name)}/add-member`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, adminUsername: user.username })
      });
      
      const data = await res.json();
      if (data.success) {
        setCurrentGroup(data.group);
        setShowAddMember(false);
        setSearchQuery('');
        setSearchResults([]);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Error adding member');
    }
  };

  const makeAdmin = async (username) => {
    if (!currentGroup || !user) return;
    
    try {
      const res = await fetch(apiUrl(`/groups/${encodeURIComponent(currentGroup.name)}/make-admin`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, adminUsername: user.username })
      });
      
      const data = await res.json();
      if (data.success) {
        setCurrentGroup(data.group);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error('Error making admin:', error);
      alert('Error making admin');
    }
  };

  const removeMember = async (username) => {
    if (!currentGroup || !user) return;
    
    if (!confirm(`Remove ${username} from the group?`)) return;
    
    try {
      const res = await fetch(apiUrl(`/groups/${encodeURIComponent(currentGroup.name)}/remove-member`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, adminUsername: user.username })
      });
      
      const data = await res.json();
      if (data.success) {
        setCurrentGroup(data.group);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Error removing member');
    }
  };

  // Search and filter
  useEffect(() => {
    if (!user) return;
    
    const availableUsers = allUsers.filter(u => u.username !== user.username);
    
    let filteredGroups = groups;
    let filteredUsers = availableUsers;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredGroups = groups.filter(g => 
        g.name.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query)
      );
      filteredUsers = availableUsers.filter(u => 
        u.username.toLowerCase().includes(query)
      );
    }
    
    // Apply tab filter
    if (activeFilter === 'Unread') {
      // Show only chats with unread messages
      filteredUsers = filteredUsers.filter(u => unreadDM[u.username] > 0);
      filteredGroups = filteredGroups.filter(g => unreadGroups[g.name] > 0);
    } else if (activeFilter === 'Groups') {
      // Show only groups
      filteredUsers = [];
    }
    
    setFilteredChats({ groups: filteredGroups, users: filteredUsers });
  }, [searchQuery, groups, allUsers, user, activeFilter, unreadDM, unreadGroups]);

  // Helper functions
  const isGroupAdmin = currentGroup && user && 
    currentGroup.members.some(m => m.username === user.username && m.role === 'admin');

  const isUserOnline = (username) => 
    onlineUsers.some(onlineUser => onlineUser.username === username);

  const getReadReceiptIcon = (msg, selectedChat) => {
    if (selectedChat.type === 'group') {
      // Group chat read receipts
      if (!msg.readBy || msg.readBy.length === 0) {
        return '✓'; // Single tick - sent
      } else if (msg.readBy.length < (currentGroup?.members?.length || 1) - 1) {
        return '✓✓'; // Double tick - partially read
      } else {
        return '✓✓'; // Double tick - read by all (will be styled blue with CSS)
      }
    } else {
      // DM read receipts - with fallback for old messages
      const status = msg.status || 'delivered'; // Default to delivered for old messages
      switch (status) {
        case 'sent': return '✓';
        case 'delivered': return '✓✓';
        case 'read': return '✓✓'; // Will be styled blue with CSS
        default: return '✓✓'; // Default to delivered
      }
    }
  };

  const getReadReceiptClass = (msg, selectedChat) => {
    if (selectedChat.type === 'group') {
      const allRead = msg.readBy && msg.readBy.length >= (currentGroup?.members?.length || 1) - 1;
      return allRead ? 'wa-read' : '';
    } else {
      const status = msg.status || 'delivered';
      return status === 'read' ? 'wa-read' : '';
    }
  };

  // Render authentication form
  if (!user) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</h2>
          <form className="auth-form" onSubmit={handleAuth}>
            <input
              className="auth-input"
              placeholder="Username"
              value={authForm.username}
              onChange={e => setAuthForm(f => ({ ...f, username: e.target.value }))}
              required
            />
            <input
              className="auth-input"
              type="password"
              placeholder="Password"
              value={authForm.password}
              onChange={e => setAuthForm(f => ({ ...f, password: e.target.value }))}
              required
            />
            {authMode === 'register' && (
              <input
                className="auth-input"
                placeholder="Avatar URL (optional)"
                value={authForm.avatar}
                onChange={e => setAuthForm(f => ({ ...f, avatar: e.target.value }))}
              />
            )}
            <button className="auth-button" type="submit">
              {authMode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
          <button
            className="auth-switch"
            onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
          >
            {authMode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    );
  }

  // Render main app
  return (
    <div className="wa-app">
      {/* Sidebar */}
      <aside className="wa-sidebar">
        <div className="header">
          <div className="wa-title">Letter</div>
          <div>
            <button className="wa-icon-btn" title="New Group" onClick={createGroup}>
              ＋
            </button>
          </div>
        </div>
        
        <div className="wa-search">
          <input
            placeholder="Search or start a new chat"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="wa-filters">
          <button 
            className={`wa-chip ${activeFilter === 'All' ? 'active' : ''}`}
            onClick={() => setActiveFilter('All')}
          >
            All
          </button>
          <button 
            className={`wa-chip ${activeFilter === 'Unread' ? 'active' : ''}`}
            onClick={() => setActiveFilter('Unread')}
          >
            Unread
          </button>
          <button 
            className={`wa-chip ${activeFilter === 'Groups' ? 'active' : ''}`}
            onClick={() => setActiveFilter('Groups')}
          >
            Groups
          </button>
        </div>
        
        <div className="wa-chat-list">
          {filteredChats.groups.map(g => (
            <div key={g.name} className="wa-chat-item" onClick={() => openGroup(g.name)}>
              <div className="wa-avatar-container">
                <div className="wa-avatar wa-group-avatar">G</div>
              </div>
              <div className="wa-item-text">
                <div className="wa-item-top">
                  <div className="wa-name">{g.name}</div>
                  {!!unreadGroups[g.name] && <div className="wa-unread">{unreadGroups[g.name]}</div>}
                </div>
                <div className="wa-preview">Group • {g.members?.length || 0} members</div>
              </div>
            </div>
          ))}
          
          {filteredChats.users.map(u => (
            <div key={u.username} className="wa-chat-item" onClick={() => openDM(u.username)}>
              <div className="wa-avatar-container">
                <div className="wa-avatar">{u.username[0]?.toUpperCase()}</div>
                {isUserOnline(u.username) && <div className="wa-online-indicator"></div>}
              </div>
              <div className="wa-item-text">
                <div className="wa-item-top">
                  <div className="wa-name">{u.username}</div>
                  {!!unreadDM[u.username] && <div className="wa-unread">{unreadDM[u.username]}</div>}
                </div>
                <div className="wa-preview">{isUserOnline(u.username) ? 'Online' : 'Last seen recently'}</div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat area */}
      <section className="wa-chat">
        {selectedChat.type ? (
          <>
            <div className="wa-chat-header">
              <div 
                className="wa-chat-title" 
                onClick={() => selectedChat.type === 'group' && setShowGroupDetails(!showGroupDetails)}
                style={{ cursor: selectedChat.type === 'group' ? 'pointer' : 'default' }}
              >
                <div className="wa-avatar">
                  {selectedChat.title?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <div className="wa-name">{selectedChat.title}</div>
                  <div className="wa-time">
                    {selectedChat.type === 'group' && currentGroup
                      ? `${currentGroup.members?.length || 0} members`
                      : selectedChat.type === 'dm' && isUserOnline(selectedChat.id)
                      ? 'Online'
                      : selectedChat.type === 'dm'
                      ? 'Last seen recently'
                      : ''
                    }
                  </div>
                </div>
              </div>
            </div>
            
            <div className="wa-messages">
              {messages.map((msg, i) => {
                const isOutgoing = msg.from === user.username;
                const prevMsg = messages[i - 1];
                const showSenderName = selectedChat.type === 'group' && 
                  !isOutgoing && 
                  (!prevMsg || prevMsg.from !== msg.from);
                
                // Color for sender name based on username
                const getSenderColor = (username) => {
                  const colors = ['#00d4ff', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'];
                  let hash = 0;
                  for (let i = 0; i < username.length; i++) {
                    hash = username.charCodeAt(i) + ((hash << 5) - hash);
                  }
                  return colors[Math.abs(hash) % colors.length];
                };
                
                return (
                  <div key={msg._id || i} className={`wa-bubble ${isOutgoing ? 'out' : 'in'}`}>
                    {showSenderName && (
                      <div 
                        className="wa-sender-name" 
                        style={{ color: getSenderColor(msg.from) }}
                      >
                        {msg.from}
                      </div>
                    )}
                    <div>{msg.message}</div>
                    <div className="wa-meta">
                      <span>{new Date(msg.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}</span>
                      {isOutgoing && (
                        <span className={`wa-read-receipt ${getReadReceiptClass(msg, selectedChat)}`}>
                          {getReadReceiptIcon(msg, selectedChat)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            
            <form className="wa-composer" onSubmit={sendMessage}>
              <input
                className="wa-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Type a message"
                autoFocus
              />
              <button className="wa-send" type="submit">
                →
              </button>
            </form>
          </>
        ) : (
          <div className="wa-empty-chat">
            <h3>Welcome to Letter</h3>
            <p>Select a chat to start messaging</p>
          </div>
        )}
      </section>

      {/* Group Details Panel */}
      {showGroupDetails && currentGroup && (
        <aside className="wa-group-details">
          <div className="wa-group-header">
            <button className="wa-icon-btn" onClick={() => setShowGroupDetails(false)}>
              ←
            </button>
            <div className="wa-title">Group Info</div>
          </div>
          
          <div className="wa-group-info">
            <div className="wa-group-avatar-large">G</div>
            <h3>{currentGroup.name}</h3>
            {currentGroup.description && (
              <p className="wa-group-desc">{currentGroup.description}</p>
            )}
            <p className="wa-group-meta">Created by {currentGroup.createdBy}</p>
          </div>

          <div className="wa-members-section">
            <div className="wa-section-header">
              <span>{currentGroup.members?.length || 0} members</span>
              {isGroupAdmin && (
                <button className="wa-icon-btn" onClick={() => setShowAddMember(true)}>
                  +
                </button>
              )}
            </div>
            
            <div className="wa-members-list">
              {currentGroup.members?.map(member => (
                <div key={member.username} className="wa-member-item">
                  <div className="wa-avatar">{member.username[0]?.toUpperCase()}</div>
                  <div className="wa-member-info">
                    <div className="wa-member-name">{member.username}</div>
                    <div className="wa-member-role">{member.role}</div>
                  </div>
                  {isGroupAdmin && member.username !== user.username && (
                    <div className="wa-member-actions">
                      {member.role === 'member' && (
                        <button onClick={() => makeAdmin(member.username)}>
                          Make Admin
                        </button>
                      )}
                      <button 
                        onClick={() => removeMember(member.username)} 
                        className="wa-danger"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="wa-modal">
          <div className="wa-modal-content">
            <div className="wa-modal-header">
              <h3>Add Member</h3>
              <button onClick={() => {
                setShowAddMember(false);
                setSearchQuery('');
                setSearchResults([]);
              }}>
                ×
              </button>
            </div>
            <div className="wa-search-input">
              <input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchUsers(e.target.value);
                }}
              />
            </div>
            <div className="wa-search-results">
              {searchResults.map(user => (
                <div 
                  key={user.username} 
                  className="wa-search-result" 
                  onClick={() => addMemberToGroup(user.username)}
                >
                  <div className="wa-avatar">{user.username[0]?.toUpperCase()}</div>
                  <div className="wa-search-name">{user.username}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className="toast">{t.text}</div>
        ))}
      </div>
    </div>
  );
}

export default App;
